import { GameState } from '../objects/GameState';
import { TiledGameObject } from 'src/games/shared/phaserHelpers/HandyTypes';
import { Diagnosis } from '../objects/Diagnosis';
import { Helpers } from '../objects/Helpers';
import initializeInteractiveCursor, { updateInteractiveCursor } from 'src/games/shared/InteractiveCursorUtil';
import TranslateApi from 'src/utils/TranslateHelper';

export class SickCellsScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    background: Phaser.GameObjects.Image;
    map: Phaser.Tilemaps.Tilemap;
    correctDiagnosis: Diagnosis;

    constructor() {
        super({
            key: "SickCellsScene_" + Math.random(),
        });
    }

    preload(): void {
        // All assets were loaded in MainScene
        this.load.image('circle', require('/src/assets/qci/sprites/circle.png'));
        this.load.tilemapTiledJSON("analyze_blood_scene_map", require("/src/assets/qci/maps/labDiagnosisGame/lab_diagnosis_analyze_blood.json"));
    }

    create(): void {
        this.initialize();
        this.generateScene();
        this.registerEvents();
        initializeInteractiveCursor(this);
    }

    registerEvents(): void {
        // Drag around the blood sample to see through the lense
        this.input.on('drag', (pointer, gameObject, dragX, dragY) => {
            gameObject.x = dragX;
            gameObject.y = dragY;
        });
    }

    initialize() {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: 'analyze_blood_scene_map' });
        this.correctDiagnosis = GameState.current.correctDiagnosis;
    }

    generateScene() {
        this.background = this.add.image(this.gameWidth / 2, this.gameHeight / 2, "pixel");
        this.background.setDisplaySize(this.gameWidth, this.gameHeight);
        this.background.setOrigin(0.5);
        this.background.setTint(0x2d2d39);

        this.addObjects();
        Helpers.addNextButton(this);
        Helpers.addBackButton(this);
    }

    addObjects(): void {
        var graphics = this.add.graphics();
        var bloodSample, lensMask;
        this.map.getObjectLayer("Scene").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "sample") {
                bloodSample = this.createBloodSample(ob, true);
            }
            else if (ob.name === "lens") {
                // Look through lens to analyze the blood
                lensMask = this.make.sprite({
                    x: ob.x,
                    y: ob.y,
                    key: 'circle',
                    add: false,
                });

                lensMask.setPosition(ob.x + ob.width / 2, ob.y + ob.height / 2);
                lensMask.setDisplaySize(ob.width, ob.height);

                // Outline of the lens
                var circle = new Phaser.Geom.Circle(lensMask.x, lensMask.y, lensMask.displayWidth / 2 + 20);
                circle.setPosition(lensMask.getCenter().x, lensMask.getCenter().y);
                graphics.fillStyle(0x000000);
                graphics.fillCircleShape(circle);
            }
            else if (ob.name === "text") {
                var title_fontStyle = { fontFamily: 'Russo One, Roboto Mono, Arial', fontSize: 20, color: '#ffffff', padding: 20 };
                var translateString = "Diagnosis.Phaser.SickCellsScene.";
                var translatedText = TranslateApi.translate(translateString + "text");
                var text = this.add.text(ob.x, ob.y, translatedText, title_fontStyle).setDepth(1);
                text.setX(this.gameWidth / 2 - text.width / 2);
                text.setWordWrapWidth(ob.width, true);
                this.add.image(text.x - 5, text.y - 5, "pixel").setDisplaySize(text.displayWidth + 10, text.displayHeight + 10).setDepth(.5).setOrigin(0).setDepth(.75).setTint(0x000000);
                this.add.image(text.x - 10, text.y - 10, "pixel").setDisplaySize(text.displayWidth + 20, text.displayHeight + 20).setDepth(.5).setOrigin(0).setTint(0x0033cc);
            }
        });
        bloodSample.mask = new Phaser.Display.Masks.BitmapMask(this, lensMask);
    }

    /**
     * Dynamically creates a "blood sample" by randomly adding different cells to a "petri dish"
     */
    createBloodSample(ob: TiledGameObject, isUnhealthy: boolean): Phaser.GameObjects.Container {
        var bloodSample = this.add.container(ob.x + ob.width / 2, ob.y + ob.height / 2);
        bloodSample.setSize(ob.width, ob.height);

        var background = this.add.image(0, 0, "circle");
        background.setTint(0xffffff);
        background.setDisplaySize(ob.width, ob.height);
        bloodSample.add(background);

        if (isUnhealthy) {
            this.addNormalCells(15, bloodSample);
            this.addSickCells(this.correctDiagnosis.sickCellColor, 5, bloodSample);
        }
        else {
            this.addNormalCells(30, bloodSample);
        }

        bloodSample.setInteractive({
            hitArea: background.getBounds(), hitAreaCallback: (pointer) => {
                return this.background.getBounds().contains(pointer.x, pointer.y);
            }
        });

        this.input.setDraggable(bloodSample);
        bloodSample.setScale(1.1);

        return bloodSample;
    }

    addNormalCells(count: number, sample: Phaser.GameObjects.Container): void {
        for (var i = 0; i < count; i++) {
            var normal_cell = this.add.image(0, 0, "assets", "temp_normal_cell");
            normal_cell.setDisplaySize(40, 40);
            normal_cell.setTint(this.correctDiagnosis.healthyCellColor);
            var maxDistanceX = sample.displayWidth / 2 - 3 * normal_cell.displayWidth;
            var maxDisanceY = sample.displayHeight / 2 - 3 * normal_cell.displayHeight;
            var x = Phaser.Math.Between(-maxDistanceX, maxDistanceX);
            var y = Phaser.Math.Between(-maxDisanceY, maxDisanceY);
            normal_cell.setPosition(x, y);
            sample.add(normal_cell);
        }
    }

    addSickCells(color: number, count: number, sample: Phaser.GameObjects.Container): void {
        for (var i = 0; i < count; i++) {
            var sick_cell = this.add.image(0, 0, "assets", "temp_sick_cell");
            sick_cell.setDisplaySize(40, 40);
            sick_cell.setTint(color);
            var maxDistanceX = sample.displayWidth / 2 - 3 * sick_cell.displayWidth;
            var maxDisanceY = sample.displayHeight / 2 - 3 * sick_cell.displayHeight;
            var x = Phaser.Math.Between(-maxDistanceX, maxDistanceX);
            var y = Phaser.Math.Between(-maxDisanceY, maxDisanceY);
            sick_cell.setPosition(x, y);
            sample.add(sick_cell);
        }
    }

    update(): void {
        updateInteractiveCursor(this);
    }
}



// WEBPACK FOOTER //
// ./src/games/DiagnosisGame/scenes/SickCellsScene.ts